import React, { useCallback, useRef, useState } from "react";
import { UseFormRegisterReturn } from "react-hook-form";
import InputValidationTooltip from "../../common/InputValidationTooltip";
import { formatPhone } from "utils/formatPhone";
import { nonDigitGlobalRegex } from "constants/validations";

interface PhoneNumberFieldProps {
    register: UseFormRegisterReturn;
    error?: string;
    value?: string;
    width?: string;
    id?: string;
    className?: string;
}

const PhoneNumberField = ({
    register,
    error,
    value = "",
    width = "flex-1 min-w-0",
    id,
    className = "",
}: PhoneNumberFieldProps) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = e.target.value;
            // Remove all non-digits
            const numericValue = inputValue.replace(nonDigitGlobalRegex, "");
            // Remove formatting characters to check if user typed invalid chars
            const inputWithoutFormatting = inputValue.replace(/[\s\-()]/g, "");

            // Only show tooltip if user typed non-digit characters (excluding formatting chars)
            if (inputWithoutFormatting !== numericValue && inputValue.length > 0) {
                inputRef.current = e.target;
                setShowTooltip(true);

                if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);

                tooltipTimeoutRef.current = setTimeout(() => {
                    setShowTooltip(false);
                }, 2000);
            }

            if (numericValue.length <= 10) {
                e.target.value = formatPhone(numericValue);
                if (register.onChange) {
                    register.onChange(e);
                }
            }
        },
        [register]
    );

    return (
        <div className={`form-control ${width}`}>
            <label className="label h-7" htmlFor={id}>
                <span className="text-base-content">Phone Number</span>
            </label>
            <input
                {...register}
                onChange={handleChange}
                ref={(e) => {
                    if (register.ref) {
                        register.ref(e);
                    }
                    inputRef.current = e;
                }}
                id={id}
                type="text"
                placeholder="(123) 456-7890"
                value={formatPhone(value)}
                className={`input input-bordered bg-base-100 h-9 text-sm ${error ? "input-error" : ""} ${className}`}
                maxLength={14}
            />
            <InputValidationTooltip message="Only numbers allowed" show={showTooltip} targetRef={inputRef} />
            {error && <p className="text-error text-xs mt-1">{error}</p>}
        </div>
    );
};

export default PhoneNumberField;
